// ═══════════════════════════════════════════════════════════════
// BLACKBOARD — Position Training Plans
// Manage training requirements for each EM position
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Text,
  Group,
  SimpleGrid,
  Badge,
  Modal,
  Button,
  Stack,
  Table,
  Loader,
  Center,
  Divider,
  ActionIcon,
  Tooltip,
  TextInput,
  Checkbox,
  Alert,
} from '@mantine/core';
import {
  IconSearch,
  IconPlus,
  IconX,
  IconDeviceFloppy,
  IconSend,
  IconDatabase,
  IconInfoCircle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useBlackboardStore } from '../../store/blackboardStore';
import {
  EM_POSITIONS,
  DEFAULT_POSITION_PLANS,
} from './utils/positions';
import type { EMPosition } from './utils/positions';
import type { PositionPlan } from '../../store/blackboardStore';

// ── Constants ────────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  'NIMS/ICS': 'blue',
  'Professional Development': 'violet',
  'Hazmat/Safety': 'orange',
  'Health & Safety': 'red',
  Operations: 'teal',
  'Community Programs': 'green',
  Weather: 'cyan',
  Communications: 'indigo',
  Custom: 'gray',
};

function getFrequencyLabel(days: number): string {
  if (days === 0) return 'One-Time';
  if (days === 365) return 'Annual';
  if (days === 730) return 'Biennial';
  if (days === 1095) return 'Triennial';
  return `${days} days`;
}

// ── Component ────────────────────────────────────────────────

export default function PositionPlans() {
  const {
    users,
    trainings,
    positionPlans,
    assignments,
    loadUsers,
    loadTrainings,
    loadPositionPlans,
    loadAssignments,
    savePositionPlan,
    createAssignment,
  } = useBlackboardStore();

  const [initialLoad, setInitialLoad] = useState(true);

  // Modal / edit state
  const [selectedPosition, setSelectedPosition] = useState<EMPosition | null>(null);
  const [editTrainingIds, setEditTrainingIds] = useState<string[]>([]);
  const [addSearch, setAddSearch] = useState('');
  const [addSelected, setAddSelected] = useState<string[]>([]);

  // Loading flags
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [initializing, setInitializing] = useState(false);

  // ── Data loading ───────────────────────────────────────────

  useEffect(() => {
    async function load() {
      await Promise.all([
        loadUsers(),
        loadTrainings(),
        loadPositionPlans(),
        loadAssignments(),
      ]);
      setInitialLoad(false);
    }
    load();
  }, [loadUsers, loadTrainings, loadPositionPlans, loadAssignments]);

  // Auto-seed position plans from defaults if none exist
  const [autoSeeded, setAutoSeeded] = useState(false);
  useEffect(() => {
    if (autoSeeded || initialLoad || positionPlans.length > 0 || trainings.length === 0) return;
    const active = trainings.filter(t => t.active === 'true' || t.active === '1');
    if (active.length === 0) return;
    setAutoSeeded(true);
    (async () => {
      setInitializing(true);
      for (const position of EM_POSITIONS) {
        const defaultPlan = DEFAULT_POSITION_PLANS[position.value];
        if (!defaultPlan) continue;
        const matched = active.filter(t =>
          defaultPlan.trainings.some(keyword => t.name.includes(keyword)),
        );
        await savePositionPlan({
          position: position.value,
          trainingIds: matched.map(t => String(t.id)).join(','),
          trainingNames: matched.map(t => t.name).join(','),
        });
      }
      setInitializing(false);
      notifications.show({ message: 'Position plans auto-initialized from defaults', color: 'green' });
    })();
  }, [initialLoad, positionPlans.length, trainings]);

  // ── Helpers ────────────────────────────────────────────────

  const activeTrainings = useMemo(
    () => trainings.filter(t => t.active === 'true' || t.active === '1'),
    [trainings],
  );

  const trainingMap = useMemo(
    () => Object.fromEntries(activeTrainings.map(t => [String(t.id), t])),
    [activeTrainings],
  );

  const hasPlans = positionPlans.length > 0;

  const getMemberCount = (positionValue: string) =>
    users.filter(u => u.position === positionValue).length;

  const getPlanForPosition = (positionValue: string): PositionPlan | undefined =>
    positionPlans.find(p => p.position.toLowerCase() === positionValue.toLowerCase());

  const getPlanTrainingIds = (plan: PositionPlan | undefined): string[] => {
    if (!plan || !plan.trainingIds) return [];
    return plan.trainingIds.split(',').map(s => s.trim()).filter(Boolean);
  };

  // ── Initialize from defaults ───────────────────────────────

  const handleInitialize = async () => {
    setInitializing(true);
    try {
      let count = 0;
      for (const position of EM_POSITIONS) {
        const defaultPlan = DEFAULT_POSITION_PLANS[position.value];
        if (!defaultPlan) continue;

        const matched = activeTrainings.filter(t =>
          defaultPlan.trainings.some(keyword => t.name.includes(keyword)),
        );

        await savePositionPlan({
          position: position.value,
          trainingIds: matched.map(t => String(t.id)).join(','),
          trainingNames: matched.map(t => t.name).join(','),
        });
        count++;
      }

      notifications.show({
        title: 'Success',
        message: `Initialized ${count} position plans from defaults`,
        color: 'green',
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to initialize plans: ' + msg, color: 'red' });
    } finally {
      setInitializing(false);
    }
  };

  // ── Open position modal ────────────────────────────────────

  const handleOpenPosition = (position: EMPosition) => {
    setSelectedPosition(position);
    const plan = getPlanForPosition(position.value);
    setEditTrainingIds(getPlanTrainingIds(plan));
    setAddSearch('');
    setAddSelected([]);
  };

  const handleCloseModal = () => {
    setSelectedPosition(null);
    setEditTrainingIds([]);
    setAddSearch('');
    setAddSelected([]);
  };

  // ── Remove training from plan ──────────────────────────────

  const handleRemoveTraining = (trainingId: string) => {
    setEditTrainingIds(prev => prev.filter(id => id !== trainingId));
  };

  // ── Add selected trainings ─────────────────────────────────

  const handleAddSelected = () => {
    setEditTrainingIds(prev => [...prev, ...addSelected]);
    setAddSelected([]);
    setAddSearch('');
  };

  const toggleAddSelection = (trainingId: string) => {
    setAddSelected(prev =>
      prev.includes(trainingId) ? prev.filter(id => id !== trainingId) : [...prev, trainingId],
    );
  };

  // ── Save changes ───────────────────────────────────────────

  const handleSave = async () => {
    if (!selectedPosition) return;
    setSaving(true);
    try {
      const names = editTrainingIds.map(id => trainingMap[id]?.name || 'Unknown');

      await savePositionPlan({
        position: selectedPosition.value,
        trainingIds: editTrainingIds.join(','),
        trainingNames: names.join(','),
      });

      notifications.show({
        title: 'Success',
        message: `Saved ${selectedPosition.label} plan (${editTrainingIds.length} trainings)`,
        color: 'green',
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to save: ' + msg, color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  // ── Push to Members ────────────────────────────────────────

  const handlePushToMembers = async () => {
    if (!selectedPosition) return;
    setPushing(true);
    try {
      const posMembers = users.filter(u => u.position === selectedPosition.value);
      if (posMembers.length === 0) {
        notifications.show({ title: 'Warning', message: 'No members found with this position.', color: 'yellow' });
        setPushing(false);
        return;
      }

      let totalAssigned = 0;
      const dueDate = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000);
      const dueDateStr = dueDate.toISOString().split('T')[0];

      for (const member of posMembers) {
        // Get existing assignments for this user
        const existingIds = new Set<string>();
        assignments
          .filter(a => a.userId === String(member.id) || a.userId === member.email)
          .forEach(a => {
            (a.trainingIds || '').split(',').forEach(tid => {
              const trimmed = tid.trim();
              if (trimmed) existingIds.add(trimmed);
            });
          });

        // Filter to trainings not yet assigned
        const missingIds = editTrainingIds.filter(tid => !existingIds.has(tid));
        if (missingIds.length === 0) continue;

        await createAssignment({
          userId: String(member.id),
          trainingIds: missingIds.join(','),
          dueDate: dueDateStr,
          notes: 'Auto-assigned from position plan',
        });
        totalAssigned += missingIds.length;
      }

      notifications.show({
        title: 'Success',
        message: `Pushed ${totalAssigned} training(s) to ${posMembers.length} member(s)`,
        color: 'green',
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to push: ' + msg, color: 'red' });
    } finally {
      setPushing(false);
    }
  };

  // ── Available trainings for adding ─────────────────────────

  const availableTrainings = useMemo(
    () =>
      activeTrainings
        .filter(t => !editTrainingIds.includes(String(t.id)))
        .filter(
          t =>
            !addSearch ||
            t.name.toLowerCase().includes(addSearch.toLowerCase()) ||
            (t.category || '').toLowerCase().includes(addSearch.toLowerCase()),
        ),
    [activeTrainings, editTrainingIds, addSearch],
  );

  // ── Render ─────────────────────────────────────────────────

  if (initialLoad) {
    return (
      <Center h={400}>
        <Loader color="blue" />
      </Center>
    );
  }

  const memberCount = selectedPosition ? getMemberCount(selectedPosition.value) : 0;

  return (
    <Box>
      {/* Page Header */}
      <Group justify="space-between" align="flex-end" mb="lg">
        <Box>
          <Text
            size="10px"
            fw={700}
            c="dimmed"
            style={{ textTransform: 'uppercase', letterSpacing: 2 }}
          >
            Administration
          </Text>
          <Text size="xl" fw={700} c="#1a365d">
            Position Training Plans
          </Text>
          <Text size="sm" c="dimmed" mt={4}>
            Manage training requirements for each emergency management position. Click a position
            to view, edit, or push training assignments.
          </Text>
        </Box>
        {!hasPlans && (
          <Button
            color="blue"
            leftSection={<IconDatabase size={13} />}
            onClick={handleInitialize}
            loading={initializing}
          >
            Initialize from Defaults
          </Button>
        )}
        {hasPlans && (
          <Button
            variant="outline"
            color="gray"
            leftSection={<IconDatabase size={13} />}
            onClick={handleInitialize}
            loading={initializing}
          >
            Reset to Defaults
          </Button>
        )}
      </Group>

      {/* Position Cards Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
        {EM_POSITIONS.map(position => {
          const plan = getPlanForPosition(position.value);
          const trainingCount = getPlanTrainingIds(plan).length;
          const members = getMemberCount(position.value);
          const PositionIcon = position.icon;

          return (
            <Paper
              key={position.value}
              p="md"
              radius="md"
              withBorder
              style={{
                borderLeft: `4px solid ${position.color}`,
                cursor: 'pointer',
                transition: 'box-shadow 0.15s ease, transform 0.15s ease',
              }}
              onClick={() => handleOpenPosition(position)}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
              }}
            >
              <Group gap="sm" mb="xs">
                <Box
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: `${position.color}18`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PositionIcon size={16} color={position.color} />
                </Box>
                <Box style={{ flex: 1 }}>
                  <Text fw={600} size="sm" c="#1a365d">
                    {position.label}
                  </Text>
                </Box>
              </Group>
              <Group gap="xs">
                <Badge size="sm" variant="light" color="blue">
                  {trainingCount} required
                </Badge>
                <Badge size="sm" variant="light" color="gray">
                  {members} member{members !== 1 ? 's' : ''}
                </Badge>
              </Group>
            </Paper>
          );
        })}
      </SimpleGrid>

      {/* Position Management Modal */}
      <Modal
        opened={!!selectedPosition}
        onClose={handleCloseModal}
        title={
          selectedPosition ? (
            <Group gap="sm">
              <Box
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: `${selectedPosition.color}18`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <selectedPosition.icon size={14} color={selectedPosition.color} />
              </Box>
              <Text fw={700} size="lg" c="#1a365d">
                {selectedPosition.label}
              </Text>
              <Badge size="sm" variant="light" color="gray">
                {memberCount} member{memberCount !== 1 ? 's' : ''}
              </Badge>
            </Group>
          ) : null
        }
        size="lg"
        centered
      >
        {selectedPosition && (
          <Stack gap="md">
            {/* Current trainings table */}
            <Text size="sm" fw={600} c="#1a365d">
              Required Trainings ({editTrainingIds.length})
            </Text>

            {editTrainingIds.length > 0 ? (
              <Box style={{ maxHeight: 300, overflowY: 'auto' }}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Training Name</Table.Th>
                      <Table.Th>Category</Table.Th>
                      <Table.Th>Frequency</Table.Th>
                      <Table.Th style={{ width: 50 }}>Remove</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {editTrainingIds.map(tid => {
                      const t = trainingMap[tid];
                      if (!t) return null;
                      return (
                        <Table.Tr key={tid}>
                          <Table.Td>
                            <Text size="sm" fw={500}>
                              {t.name}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              size="sm"
                              variant="light"
                              color={CATEGORY_COLORS[t.category] || 'gray'}
                            >
                              {t.category}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              {getFrequencyLabel(t.frequencyDays)}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Tooltip label="Remove from plan">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                size="sm"
                                onClick={() => handleRemoveTraining(tid)}
                              >
                                <IconX size={12} />
                              </ActionIcon>
                            </Tooltip>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Box>
            ) : (
              <Alert color="yellow" variant="light" icon={<IconInfoCircle size={16} />}>
                No trainings assigned to this position yet. Add trainings below or initialize from
                defaults.
              </Alert>
            )}

            <Divider />

            {/* Add Trainings Section */}
            <Text size="sm" fw={600} c="#1a365d">
              Add Trainings
            </Text>
            <TextInput
              placeholder="Search trainings by name or category..."
              leftSection={<IconSearch size={13} />}
              value={addSearch}
              onChange={e => setAddSearch(e.currentTarget.value)}
            />

            <Box
              style={{
                maxHeight: 200,
                overflowY: 'auto',
                border: '1px solid #dee2e6',
                borderRadius: 8,
                padding: 8,
              }}
            >
              {availableTrainings.length > 0 ? (
                <Stack gap={4}>
                  {availableTrainings.map(t => (
                    <Checkbox
                      key={t.id}
                      label={
                        <Group gap="xs">
                          <Text size="sm">{t.name}</Text>
                          <Badge
                            size="xs"
                            variant="light"
                            color={CATEGORY_COLORS[t.category] || 'gray'}
                          >
                            {t.category}
                          </Badge>
                        </Group>
                      }
                      checked={addSelected.includes(String(t.id))}
                      onChange={() => toggleAddSelection(String(t.id))}
                    />
                  ))}
                </Stack>
              ) : (
                <Center py="sm">
                  <Text size="sm" c="dimmed">
                    {addSearch ? 'No matching trainings found' : 'All trainings already assigned'}
                  </Text>
                </Center>
              )}
            </Box>

            {addSelected.length > 0 && (
              <Button
                variant="light"
                color="blue"
                size="sm"
                leftSection={<IconPlus size={12} />}
                onClick={handleAddSelected}
              >
                Add {addSelected.length} Selected
              </Button>
            )}

            <Divider />

            {/* Bottom actions */}
            <Group justify="space-between">
              <Button
                variant="light"
                color={selectedPosition.color}
                leftSection={<IconSend size={13} />}
                onClick={handlePushToMembers}
                loading={pushing}
              >
                Push to {memberCount} Member{memberCount !== 1 ? 's' : ''}
              </Button>
              <Group gap="sm">
                <Button
                  color="blue"
                  leftSection={<IconDeviceFloppy size={13} />}
                  onClick={handleSave}
                  loading={saving}
                >
                  Save Changes
                </Button>
                <Button variant="default" onClick={handleCloseModal}>
                  Close
                </Button>
              </Group>
            </Group>
          </Stack>
        )}
      </Modal>
    </Box>
  );
}
